import { withTrackedReducer } from '@angular-architects/ngrx-toolkit';
import { EngineReportState, INITIAL_ENGINE_REPORT_STATE } from '@navstation-portal/reporting/data-model/engine-report';
import { authEvents } from '@navstation-portal/shared/data-access/auth';
import { signalStoreFeature, type } from '@ngrx/signals';
import { on } from '@ngrx/signals/events';
import { engineReportEvents } from './engine-report-events';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function withEngineReportReducer<_>() {
  return signalStoreFeature(
    type<{ state: EngineReportState }>(),
    withTrackedReducer<EngineReportState>(
      on(engineReportEvents.createReport, () => ({
        creatingReport: true,
        report: null,
      })),
      on(engineReportEvents.createReportSuccess, ({ payload: { localReportId, reportType } }) => ({
        localReportId,
        reportType,
        creatingReport: false,
        report: { id: localReportId, details: {} },
      })),
      on(engineReportEvents.openReport, () => ({
        report: null,
        loadingReport: true,
        reportNotFound: false,
      })),
      on(engineReportEvents.openReportSuccess, ({ payload: { report } }) => ({
        report,
        loadingReport: false,
      })),
      on(engineReportEvents.openReportFailed, () => ({
        loadingReport: false,
        reportNotFound: true,
      })),
      on(engineReportEvents.selectEngine, ({ payload: { engineIndex } }, state: EngineReportState) => {
        if (!state.report) return {};

        return {
          report: {
            ...state.report,
            details: {
              ...state.report.details,
              mainEngine: engineIndex,
            },
          },
        };
      }),
      on(engineReportEvents.updateReportDetails, ({ payload: details }, state: EngineReportState) => {
        if (!state.report) return {};

        return {
          report: {
            ...state.report,
            details: {
              ...state.report.details,
              ...details,
            },
          },
        };
      }),
      on(engineReportEvents.updateCylinderUnit, ({ payload: { index, changes } }, state: EngineReportState) => {
        if (!state.report) return {};

        const cyl = [...(state.report.cylinderUnits?.cyl ?? [])];
        // Fill up to the index: a sparse array serializes its holes as null.
        while (cyl.length <= index) cyl.push({});
        cyl[index] = { ...cyl[index], ...changes };

        return {
          report: {
            ...state.report,
            cylinderUnits: {
              ...state.report.cylinderUnits,
              cyl,
            },
          },
        };
      }),
      on(engineReportEvents.trimCylinderUnits, ({ payload: { count } }, state: EngineReportState) => {
        const cyl = state.report?.cylinderUnits?.cyl;
        if (!state.report || !cyl || cyl.length <= count) return {};

        return {
          report: {
            ...state.report,
            cylinderUnits: {
              ...state.report.cylinderUnits,
              cyl: cyl.slice(0, count),
            },
          },
        };
      }),
      on(engineReportEvents.setComments, ({ payload: comments }, state: EngineReportState) => {
        if (!state.report) return {};

        return {
          report: {
            ...state.report,
            comments,
          },
        };
      }),
      on(engineReportEvents.reset, () => INITIAL_ENGINE_REPORT_STATE),
      on(authEvents.logoutSuccess, () => INITIAL_ENGINE_REPORT_STATE)
    )
  );
}
